import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination
} from '@material-ui/core';
import api from 'services/api';

const ROWS_PER_PAGE = 50;

function formatDateTime(value) {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

export default function AdmSisarePassengerAnalytics() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalLocalizadores, setTotalLocalizadores] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const scrollPageToTop = () => {
    const startPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
    const duration = 650;
    const startTime = performance.now();

    const easeInOutCubic = progress => (
      progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2
    );

    const animateScroll = currentTime => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);

      window.scrollTo(0, Math.round(startPosition * (1 - easedProgress)));

      if (progress < 1) {
        window.requestAnimationFrame(animateScroll);
      }
    };

    window.requestAnimationFrame(animateScroll);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    scrollPageToTop();
  };

  useEffect(() => {
    let mounted = true;

    async function loadAnalytics() {
      setLoading(true);
      setError('');

      try {
        const response = await api.get('/appcli/pages', {
          params: {
            analytics: 1,
            page: page + 1,
            search: debouncedSearch || undefined
          }
        });

        if (!mounted) return;

        const payload = response.data || {};
        setRows(Array.isArray(payload.data) ? payload.data : []);
        setTotalRecords(Number(payload.info?.totalrecords || 0));
        setTotalLocalizadores(Number(payload.info?.totalLocalizadores || 0));
      } catch (requestError) {
        if (!mounted) return;

        setRows([]);
        setTotalRecords(0);
        setTotalLocalizadores(0);
        setError(
          requestError.response?.data?.message
          || 'Não foi possível carregar os acessos do Sisare Passenger.'
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAnalytics();

    return () => {
      mounted = false;
    };
  }, [page, debouncedSearch]);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Analytics | Sisare Passenger
      </Typography>

      <Box mt={3} mb={2}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={9}>
            <TextField
              fullWidth
              variant="outlined"
              label="Buscar por localizador, passageiro ou OS"
              value={search}
              onChange={event => setSearch(event.target.value)}
              helperText="Sem pesquisa, são exibidos os 50 acessos mais recentes."
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Card style={{ height: '100%' }}>
              <Box
                height="100%"
                minHeight={72}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                px={2}
              >
                <Typography variant="caption" color="textSecondary">
                  Localizadores que já se conectaram
                </Typography>
                <Typography variant="h4">
                  {totalLocalizadores.toLocaleString('pt-BR')}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Box mb={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Card>
        <Box position="relative">
          {loading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              minHeight={180}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="rgba(255, 255, 255, 0.7)"
              zIndex={1}
            >
              <CircularProgress size={32} />
            </Box>
          )}

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Página</TableCell>
                <TableCell>Localizador</TableCell>
                <TableCell>Nome do Pax</TableCell>
                <TableCell>OS</TableCell>
                <TableCell>Primeiro Acesso</TableCell>
                <TableCell>Último Acesso</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhum registro encontrado.
                  </TableCell>
                </TableRow>
              )}

              {rows.map((item, index) => (
                <TableRow
                  key={`${item.ID || item.id || 'row'}-${index}`}
                  hover
                >
                  <TableCell>Login Sisare Passenger</TableCell>
                  <TableCell>
                    <strong>{item.LOCALIZADOR || item.localizador || '-'}</strong>
                  </TableCell>
                  <TableCell>{item.NOMEPAX || item.nomepax || '-'}</TableCell>
                  <TableCell>{item.CODOS || item.codos || '-'}</TableCell>
                  <TableCell>
                    {formatDateTime(item.PRIMEIROACESSO || item.primeiroacesso)}
                  </TableCell>
                  <TableCell>
                    {formatDateTime(item.ULTIMOACESSO || item.ultimoacesso)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          labelRowsPerPage="Registros por página"
          labelDisplayedRows={({ from, to }) => `${from}-${to}`}
        />
      </Card>
    </Box>
  );
}
